"use client";

import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Box, Button, Link, Typography } from "@mui/material";
import SouthIcon from "@mui/icons-material/South";
import { PORTAL_MAX_WIDTH, portalFont } from "../partnerTheme";

const ACCENT = "#FF5A5F";
const LINE = "rgba(255,255,255,0.12)";
const MUTED = "rgba(244,245,247,0.62)";
const CARD_BG = "rgba(255,255,255,0.04)";
const RADIUS = "16px";
const WAITLIST_BASE = 39394;

const IMG = {
  bestShows: "https://storage.googleapis.com/cuo-website/Best+Comedy+Shows+.jpg",
  marcelloParagon:
    "https://storage.googleapis.com/cuo-website/Marcello+Hernandez+at+Paragon.jpg",
  chrisWilson: "https://storage.googleapis.com/cuo-website/Chris+Distefano+at+Wilson.jpg",
  desert5: "https://storage.googleapis.com/cuo-website/Desert+5.jpg",
  laughingAudience: "https://storage.googleapis.com/cuo-website/UO_112923-024.jpg",
  youngCrowd: "https://storage.googleapis.com/cuo-website/UO_071324-080.jpg",
  leslieJones: "https://storage.googleapis.com/cuo-website/Leslie+Jones+KidSuper.jpg",
  bankEvent: "https://storage.googleapis.com/cuo-website/Bank%20Private%20Event.jpg",
  esbTickets: "https://storage.googleapis.com/cuo-website/ESB%20Tickets.jpg",
  verizon: "https://storage.googleapis.com/cuo-website/Verizon%20Logo.jpg",
  kidsuper: "https://storage.googleapis.com/cuo-website/KidSuper+Logo.png",
  arizona: "https://storage.googleapis.com/cuo-website/AriZona+Image.png",
  kiehls: "https://storage.googleapis.com/cuo-website/Kiehls+Image.png",
  nuuly: "https://storage.googleapis.com/cuo-website/Nuuly+Image.png",
  polymarket: "https://storage.googleapis.com/cuo-website/Polymarket%20Logo.png",
  kalshi: "https://storage.googleapis.com/cuo-website/Kalshi%20Logo%20Thumbnail.png",
  morgan: "https://storage.googleapis.com/cuo-website/Morgan%20and%20Morgan%20Image.jpg",
  grubhub: "https://storage.googleapis.com/cuo-website/Grubhub%20Logo.png",
} as const;

const OVERVIEW_NAV = [
  { id: "who-we-are", label: "Who we are" },
  { id: "how-it-works", label: "How a night works" },
  { id: "demand", label: "Demand" },
  { id: "demographics", label: "Demographics" },
  { id: "comedy-space", label: "The comedy space" },
  { id: "partners", label: "Partners" },
] as const;

const PARTNER_LOGOS = [
  { src: IMG.polymarket, alt: "Polymarket", size: "large" },
  { src: IMG.kalshi, alt: "Kalshi", size: "large" },
  { src: IMG.morgan, alt: "Morgan & Morgan", size: "large" },
  { src: IMG.grubhub, alt: "Grubhub", size: "large" },
  { src: "/images/brand-RedBull.png", alt: "Red Bull" },
  { src: "/images/brand-dr-squatch.png", alt: "Dr. Squatch" },
  { src: "/images/brand-skrewball.png", alt: "Skrewball", size: "huge", offsetY: 18 },
  { src: "/images/brand-wilson.png", alt: "Wilson", size: "xl" },
  { src: IMG.verizon, alt: "Verizon", size: "large" },
  { src: "/images/brand-Malibu-logo.png", alt: "Malibu", size: "xl" },
  { src: "/images/brand-Jameson-Logo.png", alt: "Jameson" },
  { src: "/images/brand-topo_chico.png", alt: "Topo Chico" },
  { src: "/images/brand-glenlivet.png", alt: "The Glenlivet" },
  { src: "/images/brand-guayaki.png", alt: "Guayakí" },
  { src: IMG.kidsuper, alt: "KidSuper", size: "xl" },
  { src: "/images/brand-shake-shack.png", alt: "Shake Shack", size: "xxl" },
  { src: IMG.arizona, alt: "AriZona", size: "xxl" },
  { src: IMG.kiehls, alt: "Kiehl's" },
  { src: IMG.nuuly, alt: "Nuuly", size: "xl" },
  { src: "/images/brand-The-Nudge.png", alt: "The Nudge" },
] as const;

function partnerLogoDims(size?: "large" | "xl" | "xxl" | "huge") {
  if (size === "huge") {
    return {
      maxHeight: { xs: 140, md: 190 },
      maxWidth: { xs: 320, md: 420 },
    };
  }
  if (size === "xxl") {
    return {
      maxHeight: { xs: 110, md: 150 },
      maxWidth: { xs: 280, md: 360 },
    };
  }
  if (size === "xl") {
    return {
      maxHeight: { xs: 72, md: 96 },
      maxWidth: { xs: 200, md: 260 },
    };
  }
  if (size === "large") {
    return {
      maxHeight: { xs: 52, md: 68 },
      maxWidth: { xs: 160, md: 200 },
    };
  }
  return {
    maxHeight: { xs: 36, md: 44 },
    maxWidth: { xs: 120, md: 140 },
  };
}

const CULTURE_ARTICLES = [
  {
    outlet: "Vogue",
    title: "How exclusive access became the new Birkin",
    href: "https://www.vogue.com/article/how-exclusive-access-became-the-new-birkin",
    image:
      "https://assets.vogue.com/photos/6a7ca13b35bce5db1ac41bc1/16:9/w_1280,c_limit/status-marketing-vogue-business-social-newsletter.jpg",
    blurb: "Status is shifting from what you own to where you can go, and who gets in the room.",
  },
  {
    outlet: "Pollstar · 2026",
    title: "Welcome to Pollstar’s 2026 Comedy Special: $837 million worldwide can’t be wrong",
    href: "https://news.pollstar.com/2026/01/16/welcome-to-the-world-of-comedy-making-the-world-a-better-place-one-joke-at-a-time/",
    image: "https://static.pollstar.com/wp-content/uploads/2026/01/6.-State-Farm-Arena-11.15.25-1200x800.jpg",
    blurb: "Comedy headliners reached $837M worldwide and 12.3M fans in the 2025 chart year.",
  },
  {
    outlet: "Axios",
    title: "Live comedy is booming, and everyone is winning",
    href: "https://www.axios.com/2024/05/10/comedy-industry-boom-growth-revenue-netflix",
    image: "https://i.cbc.ca/ais/1.7248722,1719607847000/full/max/0/default.jpg",
    blurb: "Pollstar data puts live comedy ticket sales near $1B, nearly tripled over a decade.",
  },
] as const;

const TREND_STATS = [
  {
    eyebrow: "Live comedy market",
    stat: "~$900M+",
    title: "Ticket revenue, nearly tripled in a decade",
    body: "Pollstar data cited by Bloomberg and Axios puts live stand-up past $900M in annual ticket sales after nearly tripling over ten years.",
    source: "Axios · May 2024",
    href: "https://www.axios.com/2024/05/10/comedy-industry-boom-growth-revenue-netflix",
    alsoHref: "https://www.bloomberg.com/news/newsletters/2024-05-05/stand-up-comedy-has-tripled-in-size-over-the-last-decade",
    alsoLabel: "Bloomberg",
  },
  {
    eyebrow: "Touring demand",
    stat: "$837M",
    title: "Worldwide comedy headliner grosses, Pollstar 2026",
    body: "In its 2026 Comedy Special, Pollstar reports comedy headliners reached $837M worldwide and performed for 12.3M fans in the 2025 chart year, with more than 6,000 reported comedy performances for the third year running.",
    source: "Pollstar · 2026",
    href: "https://news.pollstar.com/2026/01/16/welcome-to-the-world-of-comedy-making-the-world-a-better-place-one-joke-at-a-time/",
  },
  {
    eyebrow: "Status & culture",
    stat: "92%",
    title: "Fans want a deeper connection to artists they love",
    body: "“Research shows that 92% of music fans want to feel a deeper connection to their favorite artists, so we create experiences that bring fans closer to the artists they love.”",
    source: "via Vogue",
    href: "https://www.vogue.com/article/how-exclusive-access-became-the-new-birkin",
  },
] as const;

const DEMOGRAPHICS = [
  {
    source: "Instagram",
    label: "Age breakdown",
    value: "63.0%",
    detail: "ages 25–44",
  },
  {
    source: "Instagram",
    label: "Gender",
    value: "77.4%",
    detail: "women · 22.6% men",
  },
  {
    source: "Instagram",
    label: "Top country",
    value: "82.3%",
    detail: "United States",
  },
  {
    source: "YouTube",
    label: "Age breakdown",
    value: "63.3%",
    detail: "ages 25–44 · last 28 days",
  },
  {
    source: "YouTube",
    label: "Gender",
    value: "83.6%",
    detail: "men · 16.4% women · last 28 days",
  },
  {
    source: "Snapchat",
    label: "Engaged story audience",
    value: "62%",
    detail: "ages 18–34",
  },
] as const;

const NIGHT_STEPS = [
  {
    title: "The drop",
    body: "Tickets release like a sneaker drop. Guests on the waitlist compete for a limited allocation, then show up ready for a night that does not feel like a standard comedy club.",
  },
  {
    title: "Arrival & hospitality",
    body: "Doors open into an iconic space. Open-bar hospitality, light bites or chef-driven food, and time to settle in before the first set.",
  },
  {
    title: "The show",
    body: "At least four comics and one host, personally vetted by our founders. The room stays intimate even when the address is famous.",
  },
  {
    title: "The after",
    body: "Depending on the venue, guests stay for an afterparty, a tour, or another branded moment that extends the night beyond the set.",
  },
] as const;

export default function AirbnbBrandOverview({
  onContinueToResidency,
}: {
  onContinueToResidency: () => void;
}) {
  return (
    <Box>
      <OverviewHero />
      <OverviewNav />
      <WhoWeAreSection />
      <HowItWorksSection />
      <DemandSection />
      <DemographicsSection />
      <ComedySpaceSection />
      <TrendsTemplateSection />
      <PartnersSection />
      <BridgeToResidency onContinueToResidency={onContinueToResidency} />
    </Box>
  );
}

function OverviewHero() {
  return (
    <Box
      sx={{
        position: "relative",
        minHeight: { xs: "72vh", md: "78vh" },
        display: "flex",
        alignItems: "flex-end",
        overflow: "hidden",
        borderBottom: `1px solid ${LINE}`,
      }}
    >
      <Box
        component="img"
        src={IMG.bestShows}
        alt="ComedyUO show packed into an iconic venue"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center 35%",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.92) 100%)",
        }}
      />
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: PORTAL_MAX_WIDTH,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          pb: { xs: 5, md: 7 },
          pt: { xs: 18, md: 22 },
        }}
      >
        <Eyebrow accent>About us</Eyebrow>
        <Typography
          component="h1"
          sx={{
            fontFamily: portalFont,
            fontWeight: 700,
            color: "#fff",
            fontSize: { xs: "2.35rem", md: "4rem" },
            letterSpacing: "-0.035em",
            lineHeight: 1.02,
            maxWidth: 820,
            mb: 2,
          }}
        >
          We turn iconic spaces into the world&apos;s best comedy venues.
        </Typography>
        <Typography
          sx={{
            fontFamily: portalFont,
            color: "rgba(244,245,247,0.78)",
            fontSize: { xs: "1rem", md: "1.125rem" },
            lineHeight: 1.65,
            maxWidth: 560,
            mb: { xs: 3, md: 3.5 },
          }}
        >
          Since April 2021 we have hosted 350+ shows with 250+ comics.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
          <Button
            href="#who-we-are"
            sx={{
              fontFamily: portalFont,
              fontWeight: 600,
              textTransform: "none",
              color: "#0a0a0a",
              bgcolor: "#fff",
              borderRadius: "999px",
              px: 2.5,
              py: 1,
              "&:hover": { bgcolor: "rgba(255,255,255,0.88)" },
            }}
          >
            Meet ComedyUO
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function OverviewNav() {
  return (
    <Box
      sx={{
        position: "sticky",
        top: { xs: 64, md: 72 },
        zIndex: 30,
        bgcolor: "rgba(0,0,0,0.82)",
        backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${LINE}`,
      }}
    >
      <Box
        sx={{
          maxWidth: PORTAL_MAX_WIDTH,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          py: 1.25,
          display: "flex",
          gap: { xs: 1.5, md: 2.25 },
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {OVERVIEW_NAV.map((item) => (
          <Box
            key={item.id}
            component="a"
            href={`#${item.id}`}
            sx={{
              fontFamily: portalFont,
              fontSize: "0.625rem",
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: MUTED,
              whiteSpace: "nowrap",
              textDecoration: "none",
              "&:hover": { color: "#fff" },
            }}
          >
            {item.label}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function WhoWeAreSection() {
  return (
    <Section id="who-we-are">
      <Eyebrow accent>Who we are</Eyebrow>
      <Title>Underground Overground Comedy.</Title>
      <Body sx={{ maxWidth: 720, mb: { xs: 4, md: 5 } }}>
        We transform iconic spaces into unique, after-hours comedy venues. Our founders,
        David and Ethan, personally vet and select each comic so guests get top-notch talent in
        rooms that feel impossible to recreate.
      </Body>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
          gap: { xs: 2, md: 2.5 },
          mb: { xs: 3, md: 4 },
        }}
      >
        <Photo
          src={IMG.marcelloParagon}
          alt="Marcello Hernández performing in front of Paragon Sports signage"
          sx={{ minHeight: { xs: 240, md: 420 } }}
        />
        <Box sx={{ display: "grid", gap: { xs: 2, md: 2.5 } }}>
          <Photo
            src={IMG.chrisWilson}
            alt="Chris Distefano performing at Wilson"
            sx={{ minHeight: { xs: 180, md: 200 } }}
          />
          <Photo
            src={IMG.desert5}
            alt="ComedyUO Desert 5 Spot venue"
            sx={{ minHeight: { xs: 180, md: 200 } }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          borderTop: `1px solid ${LINE}`,
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        {[
          { label: "Shows hosted", value: "350+" },
          { label: "Comics on stage", value: "250+" },
          { label: "Per night", value: "4+ comics · 1 host" },
          { label: "Started", value: "April 2021" },
        ].map((stat, i) => (
          <Box
            key={stat.label}
            sx={{
              px: { xs: 0, md: 3 },
              py: { xs: 2.5, md: 3 },
              borderRight: {
                xs: i % 2 === 0 ? `1px solid ${LINE}` : "none",
                md: i < 3 ? `1px solid ${LINE}` : "none",
              },
              borderBottom: {
                xs: i < 2 ? `1px solid ${LINE}` : "none",
                md: "none",
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: { xs: "1.5rem", md: "1.85rem" },
                letterSpacing: "-0.03em",
                mb: 0.5,
              }}
            >
              {stat.value}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                color: MUTED,
                fontSize: "0.75rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function ComedySpaceSection() {
  return (
    <Section id="comedy-space" sx={{ pt: { xs: 2, md: 3 } }}>
      <Eyebrow accent>The comedy space</Eyebrow>
      <Title>Comedy is cultural infrastructure again.</Title>
      <Body sx={{ maxWidth: 720, mb: { xs: 4, md: 5 } }}>
        Live comedy is one of the few formats that still puts people in a room together, phones
        down, reacting in real time. The press is catching up: ticket sales are surging, young
        audiences are choosing live nights, and capital is following.
      </Body>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          gap: { xs: 2, md: 2.25 },
        }}
      >
        {CULTURE_ARTICLES.map((article) => (
          <Link
            key={article.href}
            href={article.href}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              borderRadius: RADIUS,
              border: `1px solid ${LINE}`,
              bgcolor: CARD_BG,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              color: "inherit",
              transition: "border-color 160ms ease, transform 160ms ease",
              "&:hover": {
                borderColor: "rgba(142,184,229,0.55)",
                transform: "translateY(-2px)",
              },
            }}
          >
            <Photo
              src={article.image}
              alt=""
              sx={{ aspectRatio: "16 / 10", minHeight: 160 }}
            />
            <Box sx={{ p: { xs: 2, md: 2.25 }, flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: ACCENT,
                  mb: 0.85,
                }}
              >
                {article.outlet}
              </Typography>
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 700,
                  color: "#fff",
                  fontSize: "1.0625rem",
                  mb: 0.75,
                  lineHeight: 1.3,
                }}
              >
                {article.title}
              </Typography>
              <Typography sx={{ fontFamily: portalFont, color: MUTED, lineHeight: 1.55 }}>
                {article.blurb}
              </Typography>
            </Box>
          </Link>
        ))}
      </Box>
    </Section>
  );
}

function TrendsTemplateSection() {
  return (
    <Section id="trends" sx={{ pt: { xs: 2, md: 3 } }}>
      <Eyebrow accent>Where it is going</Eyebrow>
      <Title>Key takeaways.</Title>
      <Body sx={{ maxWidth: 720, mb: { xs: 3, md: 4 } }}>
        The market signals that matter for a partnership like this: comedy ticket economics are
        up, touring demand is compounding, and exclusive access has become the new status currency.
      </Body>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          gap: { xs: 2, md: 2.25 },
        }}
      >
        {TREND_STATS.map((card) => (
          <Box
            key={card.eyebrow}
            sx={{
              borderRadius: RADIUS,
              border: `1px solid ${LINE}`,
              bgcolor: CARD_BG,
              p: { xs: 2.25, md: 2.75 },
              minHeight: { md: 260 },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: ACCENT,
                mb: 1.5,
              }}
            >
              {card.eyebrow}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: { xs: "2.4rem", md: "2.85rem" },
                letterSpacing: "-0.04em",
                lineHeight: 1,
                mb: 1,
              }}
            >
              {card.stat}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: "1.0625rem",
                mb: 1,
                lineHeight: 1.3,
              }}
            >
              {card.title}
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, lineHeight: 1.55, flex: 1 }}>
              {card.body}
            </Typography>
            <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "baseline" }}>
              <Link
                href={card.href}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  fontFamily: portalFont,
                  fontSize: "0.6875rem",
                  color: ACCENT,
                }}
              >
                {card.source}
              </Link>
              {"alsoHref" in card && card.alsoHref ? (
                <Link
                  href={card.alsoHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{
                    fontFamily: portalFont,
                    fontSize: "0.6875rem",
                    color: ACCENT,
                  }}
                >
                  {card.alsoLabel}
                </Link>
              ) : null}
            </Box>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function DemandSection() {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [count, setCount] = useState(WAITLIST_BASE);
  const startedRef = useRef(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const delays = [0, 5000, 2000, 7000];
    let cancelled = false;
    const timers: number[] = [];

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || startedRef.current) return;
        startedRef.current = true;
        let chain = 0;
        delays.forEach((delay) => {
          chain += delay;
          const id = window.setTimeout(() => {
            if (!cancelled) setCount((n) => n + 1);
          }, chain);
          timers.push(id);
        });
      },
      { threshold: 0.35 }
    );

    observer.observe(node);
    return () => {
      cancelled = true;
      observer.disconnect();
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  return (
    <Section id="demand" sx={{ pt: { xs: 2, md: 3 } }}>
      <Box ref={sectionRef}>
        <Eyebrow accent>Demand</Eyebrow>
        <Title>Demand that only grows.</Title>
        <Body sx={{ maxWidth: 720, mb: { xs: 4, md: 5 } }}>
          Guests do not casually buy tickets. They join The Underground and wait for access, while
          the list keeps climbing. Scarcity is the product.
        </Body>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" },
            gap: { xs: 2.5, md: 3 },
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              borderRadius: RADIUS,
              border: `1px solid ${LINE}`,
              bgcolor: CARD_BG,
              p: { xs: 3, md: 4 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <FlipWaitlistNumber value={count} />
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: { xs: "1.25rem", md: "1.5rem" },
                mb: 1.25,
                mt: 2,
              }}
            >
              people on the waitlist
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, lineHeight: 1.55 }}>
              The average waitlist member competes with tens of thousands of others for a ticket.
              That scarcity is why partner allocations and invitation-led access feel meaningful.
            </Typography>
          </Box>
          <Photo
            src={IMG.youngCrowd}
            alt="Young ComedyUO audience watching a packed show"
            sx={{ minHeight: { xs: 240, md: "100%" } }}
          />
        </Box>
      </Box>
    </Section>
  );
}

function FlipWaitlistNumber({ value }: { value: number }) {
  const formatted = value.toLocaleString("en-US");
  return (
    <Box
      aria-label={`${formatted} people on the waitlist`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 0.45, md: 0.6 },
        flexWrap: "nowrap",
      }}
    >
      {formatted.split("").map((char, i) =>
        char === "," ? (
          <Typography
            key={`comma-${i}`}
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              color: ACCENT,
              fontSize: { xs: "2.25rem", md: "3.25rem" },
              lineHeight: 1,
              px: { xs: 0.15, md: 0.25 },
            }}
          >
            ,
          </Typography>
        ) : (
          <FlipDigit key={`slot-${i}`} digit={char} />
        )
      )}
    </Box>
  );
}

function FlipDigit({ digit }: { digit: string }) {
  const currentRef = useRef(digit);
  const [previous, setPrevious] = useState(digit);
  const [current, setCurrent] = useState(digit);
  const [flipping, setFlipping] = useState(false);

  useEffect(() => {
    if (digit === currentRef.current) return;
    setPrevious(currentRef.current);
    setCurrent(digit);
    currentRef.current = digit;
    setFlipping(true);
    const id = window.setTimeout(() => setFlipping(false), 600);
    return () => window.clearTimeout(id);
  }, [digit]);

  const halfSx = {
    position: "absolute" as const,
    left: 0,
    width: "100%",
    height: "50%",
    overflow: "hidden",
  };

  const faceSx = {
    position: "absolute" as const,
    left: 0,
    width: "100%",
    height: "200%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: portalFont,
    fontWeight: 700,
    color: "#ececec",
    fontSize: { xs: "2.35rem", md: "3.4rem" },
    lineHeight: 1,
    letterSpacing: "-0.03em",
    background: "linear-gradient(180deg, #303030 0%, #1a1a1a 48%, #121212 100%)",
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: { xs: 34, md: 48 },
        height: { xs: 52, md: 72 },
        borderRadius: "8px",
        boxShadow: "0 10px 22px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)",
        perspective: "420px",
        bgcolor: "#0e0e0e",
        border: "1px solid rgba(255,255,255,0.1)",
        flexShrink: 0,
        transformStyle: "preserve-3d",
      }}
    >
      {/* Top half of new digit (static, revealed as flap flips away) */}
      <Box sx={{ ...halfSx, top: 0, borderRadius: "7px 7px 0 0", zIndex: 1 }}>
        <Box sx={{ ...faceSx, top: 0 }}>{current}</Box>
      </Box>
      {/* Bottom half of old digit until flip completes */}
      <Box sx={{ ...halfSx, bottom: 0, borderRadius: "0 0 7px 7px", zIndex: 1 }}>
        <Box sx={{ ...faceSx, bottom: 0 }}>{flipping ? previous : current}</Box>
      </Box>

      <Box
        aria-hidden
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "50%",
          height: "2px",
          mt: "-1px",
          bgcolor: "rgba(0,0,0,0.75)",
          zIndex: 6,
          boxShadow: "0 1px 0 rgba(255,255,255,0.05)",
        }}
      />

      {flipping ? (
        <>
          <Box
            sx={{
              ...halfSx,
              top: 0,
              borderRadius: "7px 7px 0 0",
              zIndex: 4,
              transformOrigin: "bottom center",
              backfaceVisibility: "hidden",
              boxShadow: "0 8px 16px rgba(0,0,0,0.45)",
              animation: "unitedFlipTop 0.3s ease-in forwards",
              "@keyframes unitedFlipTop": {
                from: { transform: "rotateX(0deg)" },
                to: { transform: "rotateX(-90deg)" },
              },
            }}
          >
            <Box sx={{ ...faceSx, top: 0 }}>{previous}</Box>
          </Box>
          <Box
            sx={{
              ...halfSx,
              bottom: 0,
              borderRadius: "0 0 7px 7px",
              zIndex: 3,
              transformOrigin: "top center",
              backfaceVisibility: "hidden",
              animation: "unitedFlipBottom 0.3s ease-out 0.3s forwards",
              transform: "rotateX(90deg)",
              "@keyframes unitedFlipBottom": {
                from: { transform: "rotateX(90deg)" },
                to: { transform: "rotateX(0deg)" },
              },
            }}
          >
            <Box sx={{ ...faceSx, bottom: 0 }}>{current}</Box>
          </Box>
        </>
      ) : null}
    </Box>
  );
}

function DemographicsSection() {
  return (
    <Section id="demographics" sx={{ pt: { xs: 2, md: 3 } }}>
      <Eyebrow accent>Demographics</Eyebrow>
      <Title>Who is our audience?</Title>
      <Body sx={{ maxWidth: 720, mb: { xs: 4, md: 5 } }}>
        Audience composition across ComedyUO&apos;s Instagram, YouTube, and Snapchat: prime
        adult ages, US-centered, with Instagram skewing women and YouTube skewing men.
      </Body>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" },
          gap: { xs: 2, md: 2.25 },
          mb: { xs: 3, md: 4 },
        }}
      >
        {DEMOGRAPHICS.map((item) => (
          <Box
            key={`${item.source}-${item.label}`}
            sx={{
              borderRadius: RADIUS,
              border: `1px solid ${LINE}`,
              bgcolor: CARD_BG,
              p: { xs: 2.25, md: 2.75 },
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: ACCENT,
                mb: 1.25,
              }}
            >
              {item.source} · {item.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: { xs: "2.35rem", md: "2.85rem" },
                letterSpacing: "-0.04em",
                lineHeight: 1,
                mb: 0.75,
              }}
            >
              {item.value}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                color: MUTED,
                fontSize: { xs: "1rem", md: "1.0625rem" },
                lineHeight: 1.35,
              }}
            >
              {item.detail}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: 2, md: 2.25 },
        }}
      >
        <Photo
          src={IMG.laughingAudience}
          alt="Young audience members laughing at a ComedyUO show"
          sx={{ minHeight: { xs: 220, md: 320 } }}
        />
        <Photo
          src={IMG.leslieJones}
          alt="Leslie Jones performing at a ComedyUO show"
          sx={{ minHeight: { xs: 220, md: 320 } }}
        />
      </Box>
    </Section>
  );
}

function HowItWorksSection() {
  return (
    <Section id="how-it-works" sx={{ pt: { xs: 2, md: 3 } }}>
      <Eyebrow accent>Same craft. New address every time.</Eyebrow>
      <Title>How a night typically works.</Title>
      <Body sx={{ maxWidth: 720, mb: { xs: 4, md: 5 } }}>
        Some nights include chef-prepared food, an afterparty, or another add-on. The spine stays
        consistent: scarce access, hospitality, elite comics, and a room people talk about after.
      </Body>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "0.85fr 1.15fr" },
          gap: { xs: 3, md: 4 },
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            borderRadius: RADIUS,
            overflow: "hidden",
            border: `1px solid ${LINE}`,
            bgcolor: "#121212",
            minHeight: { xs: 220, md: 0 },
            height: { xs: 240, md: "100%" },
          }}
        >
          <Box
            component="img"
            src={IMG.bankEvent}
            alt="ComedyUO private event atmosphere"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
            }}
          />
        </Box>
        <Box sx={{ borderTop: `1px solid ${LINE}` }}>
          {NIGHT_STEPS.map((step, i) => (
            <Box
              key={step.title}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "48px 1fr", md: "64px 1fr" },
                gap: 2,
                py: { xs: 2.5, md: 3 },
                borderBottom: `1px solid ${LINE}`,
              }}
            >
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 700,
                  color: ACCENT,
                  fontSize: { xs: "1.5rem", md: "1.75rem" },
                  letterSpacing: "-0.03em",
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </Typography>
              <Box>
                <Typography
                  sx={{
                    fontFamily: portalFont,
                    fontWeight: 700,
                    color: "#fff",
                    fontSize: { xs: "1.05rem", md: "1.2rem" },
                    mb: 0.75,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography sx={{ fontFamily: portalFont, color: MUTED, lineHeight: 1.55 }}>
                  {step.body}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Section>
  );
}

function PartnersSection() {
  return (
    <Section id="partners" sx={{ pt: { xs: 2, md: 3 } }}>
      <Eyebrow accent>Partners</Eyebrow>
      <Title>Brands we&apos;ve worked with.</Title>
      <Body sx={{ maxWidth: 720, mb: { xs: 4, md: 5 } }}>
        Brands use us for access, sampling, custom shows, and content series.
      </Body>

      <Box
        sx={{
          borderRadius: RADIUS,
          bgcolor: "#fff",
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 4 },
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(3, 1fr)",
            md: "repeat(4, 1fr)",
          },
          gap: { xs: 2.5, md: 3.5 },
          alignItems: "center",
          justifyItems: "center",
        }}
      >
        {PARTNER_LOGOS.map((logo) => {
          const dims = partnerLogoDims("size" in logo ? logo.size : undefined);
          const offsetY = "offsetY" in logo ? logo.offsetY : 0;
          return (
            <Box
              key={logo.alt}
              component="img"
              src={logo.src}
              alt={logo.alt}
              sx={{
                maxHeight: dims.maxHeight,
                maxWidth: dims.maxWidth,
                width: "auto",
                objectFit: "contain",
                display: "block",
                transform: offsetY ? `translateY(${offsetY}px)` : undefined,
              }}
            />
          );
        })}
      </Box>
    </Section>
  );
}

function BridgeToResidency({
  onContinueToResidency,
}: {
  onContinueToResidency: () => void;
}) {
  return (
    <Section id="to-residency" sx={{ pt: { xs: 2, md: 3 }, pb: { xs: 6, md: 8 } }}>
      <Box
        sx={{
          borderRadius: RADIUS,
          border: `1px solid ${LINE}`,
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" },
        }}
      >
        <Photo
          src={IMG.esbTickets}
          alt="Empire State Building comedy show tickets held in the air"
          sx={{ minHeight: { xs: 220, md: 360 } }}
        />
        <Box
          sx={{
            p: { xs: 3, md: 4.5 },
            bgcolor: CARD_BG,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Eyebrow accent sx={{ mb: 1.25 }}>
            Next
          </Eyebrow>
          <Typography
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              color: "#fff",
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
              mb: 1.5,
            }}
          >
            Now the residency.
          </Typography>
          <Typography sx={{ fontFamily: portalFont, color: MUTED, lineHeight: 1.55, mb: 3 }}>
            The Empire State Residency is how this model shows up as a four-night series with a
            distinct way in for Airbnb guests.
          </Typography>
          <Button
            onClick={onContinueToResidency}
            endIcon={<SouthIcon sx={{ transform: "rotate(-90deg)" }} />}
            sx={{
              alignSelf: "flex-start",
              fontFamily: portalFont,
              fontWeight: 600,
              textTransform: "none",
              color: "#0a0a0a",
              bgcolor: "#fff",
              borderRadius: "999px",
              px: 2.5,
              py: 1.1,
              "&:hover": { bgcolor: "rgba(255,255,255,0.88)" },
            }}
          >
            Open Empire State Residency
          </Button>
        </Box>
      </Box>
    </Section>
  );
}

function Section({
  id,
  children,
  sx = {},
}: {
  id: string;
  children: ReactNode;
  sx?: Record<string, unknown>;
}) {
  return (
    <Box
      id={id}
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2.5, md: 4 },
        py: { xs: 5, md: 7 },
        scrollMarginTop: { xs: 120, md: 140 },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function Eyebrow({
  children,
  accent = false,
  sx = {},
}: {
  children: ReactNode;
  accent?: boolean;
  sx?: Record<string, unknown>;
}) {
  return (
    <Typography
      sx={{
        fontFamily: portalFont,
        fontSize: "0.6875rem",
        fontWeight: 600,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: accent ? ACCENT : MUTED,
        mb: 1.5,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

function Title({
  children,
  sx = {},
}: {
  children: ReactNode;
  sx?: Record<string, unknown>;
}) {
  return (
    <Typography
      component="h2"
      sx={{
        fontFamily: portalFont,
        fontWeight: 700,
        color: "#fff",
        fontSize: { xs: "1.85rem", md: "2.75rem" },
        letterSpacing: "-0.03em",
        lineHeight: 1.08,
        mb: 1.5,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

function Body({
  children,
  sx = {},
}: {
  children: ReactNode;
  sx?: Record<string, unknown>;
}) {
  return (
    <Typography
      sx={{
        fontFamily: portalFont,
        color: MUTED,
        fontSize: { xs: "0.975rem", md: "1.0625rem" },
        lineHeight: 1.65,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

function Photo({
  src,
  alt,
  sx = {},
}: {
  src: string;
  alt: string;
  sx?: Record<string, unknown>;
}) {
  return (
    <Box
      sx={{
        borderRadius: RADIUS,
        overflow: "hidden",
        border: `1px solid ${LINE}`,
        bgcolor: "#121212",
        ...sx,
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    </Box>
  );
}
